/**
 * Canonical status palette — the single source of truth for status colours
 * across the portal.
 *
 * Mirrors the designer-approved `.status-pill--*` set in global.css exactly:
 * colours reference the same design tokens (--color-success/warning/error/info-bg/text)
 * so a status looks identical on every screen. The three token-less pills
 * (draft, viewed, cancelled/voided) carry the same literal values as the CSS.
 *
 * StatusBadge (components/DataTable.tsx) falls back to this when a caller passes
 * a `status` with no `config`. Per-feature STATUS_CONFIG objects are being
 * migrated onto this (Phase 4 of the front-end consistency work). Add a new
 * status here — never hand-pick a colour inline.
 */

export interface StatusStyle {
  label: string;
  bg: string;
  color: string;
  icon?: string;
}

/** Humanise a raw status key: "in_progress" -> "In Progress". */
function humanise(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Token-driven tones (match the status-pill modifier rules in global.css).
const SUCCESS = { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' };
const WARNING = { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' };
const ERROR = { bg: 'var(--color-error-bg)', color: 'var(--color-error-text)' };
const INFO = { bg: 'var(--color-info-bg)', color: 'var(--color-info-text)' };
const SLATE = { bg: 'var(--color-bg)', color: 'var(--color-text-light)' };
const SLATE_DARK = { bg: 'var(--color-bg)', color: 'var(--color-sidebar-hover)' };
const VIOLET = { bg: '#ede9fe', color: 'var(--color-primary-light)' };

/** Keyed by lowercase status string. */
export const CANONICAL_STATUS_CONFIG: Record<string, StatusStyle> = {
  draft: { label: 'Draft', ...SLATE_DARK },
  pending: { label: 'Pending', ...WARNING },
  sent: { label: 'Sent', ...INFO },
  confirmed: { label: 'Confirmed', ...INFO },
  viewed: { label: 'Viewed', ...VIOLET },
  partial: { label: 'Partial', ...WARNING },
  in_progress: { label: 'In Progress', ...INFO },
  active: { label: 'Active', ...SUCCESS },
  paid: { label: 'Paid', ...SUCCESS },
  completed: { label: 'Completed', ...SUCCESS },
  approved: { label: 'Approved', ...SUCCESS },
  accepted: { label: 'Accepted', ...SUCCESS },
  onboarded: { label: 'Onboarded', ...SUCCESS },
  overdue: { label: 'Overdue', ...ERROR },
  failed: { label: 'Failed', ...ERROR },
  rejected: { label: 'Rejected', ...ERROR },
  cancelled: { label: 'Cancelled', ...SLATE },
  voided: { label: 'Voided', ...SLATE },
  expired: { label: 'Expired', ...SLATE },
};

/**
 * Look up the canonical style for a status string (case-insensitive). Falls
 * back to a neutral grey pill with a humanised label for unknown statuses, so
 * a never-before-seen value still renders consistently instead of bare.
 */
export function getStatusStyle(status: string | null | undefined): StatusStyle {
  const key = (status || '').trim().toLowerCase();
  return (
    CANONICAL_STATUS_CONFIG[key] || {
      label: status ? humanise(key) : '—',
      bg: 'var(--color-border)',
      color: 'var(--color-text-light)',
    }
  );
}
